// Leaf module — must not import other @listening/env modules (mode.ts and
// config-keys*.ts all build on this).
import { Config, ConfigProvider, Duration } from 'effect';

/**
 * A Config that carries its env-var name (and a value encoder) at the type
 * level. This is what makes overrides typesafe end-to-end: scripts
 * (`overridesConfigProvider`), k8s env blocks (`k8sEnvVar`), and IaC/Vite
 * env threading can all reference the key instead of retyping the string.
 *
 * `A` is the read type; `In` is the type accepted when SETTING the key.
 * They differ for optional keys: `PG_STATEMENT_TIMEOUT` reads as
 * `Option<Duration>` but is set with a `Duration`.
 */
export interface ConfigKey<
  A,
  In = A,
  Name extends string = string,
> extends Config.Config<A> {
  readonly envVarName: Name;
  readonly encode: (value: In) => string;
}

/**
 * Wrap a config in env-var-name metadata. Must be the OUTERMOST wrapper —
 * piping the result through further combinators returns a plain Config and
 * drops the metadata, so `make` should return the finished key.
 *
 * Counts, sizes and versions must use `Config.int` (= `Schema.Int`, rejects
 * floats). `Config.number` is `Schema.Number` and accepts `1.5`, `NaN` and
 * `Infinity` — reserve it for genuinely fractional values.
 *
 * @example
 * export const forceAlignerMaxConcurrencyConfig = defineConfigKey(
 *   'MAX_CONCURRENCY',
 *   (name) => Config.Int(name).pipe(Config.withDefault(8)),
 * );
 */
export const defineConfigKey = <A, In = A, const Name extends string = string>(
  envVarName: Name,
  make: (envVarName: string) => Config.Config<A>,
  options?: { readonly encode?: (value: In) => string },
): ConfigKey<A, In, Name> =>
  Object.assign(make(envVarName), {
    envVarName,
    encode: options?.encode ?? (String as (value: In) => string),
  });

/**
 * Encoder for Duration-valued keys. `Duration.format` output ("1m 30s") is
 * not parseable by Config.duration, so encode as millis which always is.
 */
export const encodeDuration = (duration: Duration.Input): string =>
  `${Duration.toMillis(duration)} millis`;

/** An encoded (name, value) pair — build with {@link configKeyOverride}. */
export interface ConfigKeyOverride {
  readonly envVarName: string;
  readonly encodedValue: string;
}

/** Typecheck an override value against its key, erasing to an encoded pair. */
export const configKeyOverride = <A, In>(
  key: ConfigKey<A, In>,
  value: In,
): ConfigKeyOverride => ({
  envVarName: key.envVarName,
  encodedValue: key.encode(value),
});

/**
 * App-wide replacement for bare `ConfigProvider.fromEnv()`. v4's default env
 * source is `{...process.env, ...import.meta.env}` — and under Vite,
 * `import.meta.env` is statically inlined into every bundled module
 * (including effect itself), so its reserved metadata keys leak in as config.
 * Those keys describe the BUILD, not the app: `MODE` is 'production' for any
 * production-mode Vite build regardless of stage, which made every browser
 * bundle resolve production mode defaults (live Stripe price IDs on the dev
 * web app). Only VITE_-prefixed keys are user-provided env vars, so merge
 * exactly those and let `process.env` (absent in browsers) carry the rest.
 */
export const envConfigProvider = (
  // Test seam only: under vitest, `import.meta.env` is a write-through view
  // of `process.env`, so the browser shape (import.meta.env without
  // process.env) can't be simulated by mutating the real globals.
  sources?: {
    readonly processEnv?: Record<string, string | undefined>;
    readonly importMetaEnv?: Record<string, unknown>;
  },
): ConfigProvider.ConfigProvider => {
  const processEnv = sources
    ? sources.processEnv
    : typeof process === 'undefined'
      ? undefined
      : process.env;
  let importMetaEnv = sources?.importMetaEnv;
  if (!sources) {
    try {
      // Typed structurally, not from ambient `ImportMeta.env`: consumers that
      // load neither vite/client nor bun-types (desktop, browser-ext, mobile)
      // compile this source directly.
      importMetaEnv = (
        import.meta as ImportMeta & { env?: Record<string, unknown> }
      ).env;
    } catch {
      // import.meta.env unavailable (non-Vite context)
    }
  }
  const env: Record<string, string> = {};
  for (const [key, value] of Object.entries(processEnv ?? {})) {
    if (value !== undefined) env[key] = value;
  }
  if (importMetaEnv) {
    for (const key of Object.keys(importMetaEnv)) {
      const value = importMetaEnv[key];
      if (key.startsWith('VITE_') && typeof value === 'string') {
        env[key] = value;
      }
    }
  }
  return ConfigProvider.fromEnv({ env });
};

/**
 * Typesafe replacement for hand-built `ConfigProvider.fromMap`.
 * Overrides win over env vars by default (the "force this script to always
 * run with X" use case); pass `fallbackToEnv: false` for a sealed provider
 * where unlisted keys resolve only from their defaults.
 */
export const overridesConfigProvider = (
  overrides: ReadonlyArray<ConfigKeyOverride>,
  options?: { readonly fallbackToEnv?: boolean },
): ConfigProvider.ConfigProvider => {
  const fromMap = ConfigProvider.fromUnknown(
    Object.fromEntries(overrides.map((o) => [o.envVarName, o.encodedValue])),
  );
  return (options?.fallbackToEnv ?? true)
    ? fromMap.pipe(ConfigProvider.orElse(envConfigProvider()))
    : fromMap;
};

/**
 * Typesafe k8s container env entry. For deploy-time values (bucket names,
 * ARNs) keep the object literal but take the name from the key:
 * `{ name: someBucketConfig.envVarName, value: output }`.
 */
export const k8sEnvVar = <A, In>(
  key: ConfigKey<A, In>,
  value: In,
): { name: string; value: string } => ({
  name: key.envVarName,
  value: key.encode(value),
});
